<template>
  <div id="uploader">
    <div v-if="!loading">
      <form
        method="post"
        @submit.prevent="upload($event)"
        action="/survey"
        enctype="multipart/form-data"
      >
        <div id="upload-file">
          <label :class="{ active: uploadText.length }" for="file">
            <p v-if="!uploadText.length">{{ parseTranslation(SELECT_FILE) }}</p>
            <p v-else>
              {{
                uploadText.length > 25 ? uploadText.substring(0, 25) + '...' : uploadText
              }}
            </p>
          </label>
          <input
            @change="initHome"
            type="file"
            id="file"
            name="file"
            required
            accept=".docx,.txt"
          />
          <span id="files-type">
            *{{ parseTranslation(ACCEPTED_FILES) }} .txt, .docx
          </span>
        </div>
        <div id="survey-name">
          <input
            class="survey-name-input"
            :placeholder="translations[language][PLACEHOLDER_SURVEY_NAME]"
            v-model="name"
            type="text"
            id="name"
            name="name"
            required
            @keyup="validateName('#name')"
          />
        </div>
        <div>
          <select id="language" name="lang" v-model="surveyLanguage" required>
            <option disabled selected value="">{{
              translations[language][LANGUAGE]
            }}</option>
            <option value="pt">{{ parseTranslation(LANGUAGE_PORTUGUESE) }}</option>
            <option value="es">{{ parseTranslation(LANGUAGE_SPANISH) }}</option>
            <option value="en">{{ parseTranslation(LANGUAGE_ENGLISH) }}</option>
          </select>
        </div>
        <div>
          <select id="type" name="type" v-model="type" required>
            <option disabled selected value="">{{ parseTranslation(TYPE) }}</option>
            <option value="CAWI">CAWI</option>
            <option value="CATI">CATI</option>
            <option value="CAPI">CAPI</option>
          </select>
        </div>
        <div id="btn-upload">
          <button type="submit">Upload</button>
        </div>
      </form>
    </div>

    <Loader v-else />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import Loader from '@/components/loading/Loader-2'

import { uploadFile } from '@/services/api'

import * as translationCreate from './CreateNew.translations'

const { default: translations, ...translationKeys } = translationCreate

export default {
  name: 'uploader',
  components: {
    Loader,
  },
  data() {
    return {
      uploadText: '',
      loading: false,
      name: '',
      surveyLanguage: '',
      type: '',
      file: null,
      translations,
      ...translationKeys,
    }
  },
  methods: {
    ...mapActions([
      'surveyRegister',
      'sendMessage',
      'toggleLoginModal',
      'executeSurveyAPICall',
    ]),

    validateName(element) {
      document.querySelector(element).value = document
        .querySelector(element)
        .value.replace(/\\/g, '')
    },

    async upload() {
      if (this.loading) return

      if (!this.name || !this.surveyLanguage || !this.type || !this.file) {
        return this.sendMessage(this.translations[this.language][this.REQUIRED_FIELDS])
      }

      this.loading = true

      const { key } = await uploadFile(this.file, {
        type: 'PRIVATE',
        prefix: 'auto-tag',
      })

      const survey = await this.executeSurveyAPICall({
        method: 'POST',
        path: '/survey',
        payload: {
          lang: this.surveyLanguage,
          type: this.type,
          name: this.name,
          fileKey: key,
        },
      })

      if (!survey) return (this.loading = false)

      this.surveyRegister(survey)

      this.$router.push('/editor')

      this.loading = false
    },

    initHome() {
      const upload = document.querySelector('input[type=file]')
      const label = document.querySelector('label[for=file]')

      const allowed = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'test/plain',
      ]

      if (upload.files.length && allowed.includes(upload.files[0].type)) {
        label.classList.add('input-selected')
        this.uploadText = upload.files[0].name
        this.file = upload.files[0]
      } else {
        label.classList.remove('input-selected')
        this.uploadText = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#uploader {
  form {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
    @include d(s) {
      flex-direction: column;
    }
  }
}
#upload-file {
  position: relative;
  margin-right: 30px;
  @include d(s) {
    margin-right: 0px;
    margin-bottom: 30px;
  }
  label {
    display: block;
    min-width: 130px;
    position: relative;
    background: $gray-4;
    cursor: pointer;
    @include tipography(16);
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 10px;
    border-radius: 3px;
    &.active {
      background: $blue;
    }
  }
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    opacity: 0;
  }
  span {
    @include tipography(10);
    display: flex;
    width: 100%;
    justify-content: center;
    position: absolute;
    bottom: -20px;
  }
}
#survey-name {
  flex: 1;
  max-width: 350px;
  margin-right: 30px;
  @include d(s) {
    margin-right: 0;
    width: 300px;
    margin-bottom: 20px;
  }
  input {
    width: 100%;
    color: $white;
  }
  ::placeholder {
    color: $white;
    opacity: 1;
  }

  ::-webkit-input-placeholder {
    color: $white;
  }

  :-moz-placeholder {
    color: $white;
  }

  ::-moz-placeholder {
    color: $white;
  }

  :-ms-input-placeholder {
    color: $white;
  }
  .light & {
    input {
      color: $gray-6;
    }
    ::placeholder {
      color: $gray-6;
      opacity: 1;
    }
    ::-webkit-input-placeholder {
      color: $gray-6;
    }

    :-moz-placeholder {
      color: $gray-6;
    }

    ::-moz-placeholder {
      color: $gray-6;
    }

    :-ms-input-placeholder {
      color: $gray-6;
    }
  }
}

.survey-name-input,
#language,
#type {
  @include tipography(16);
  color: $gray-5;
  background: none;
  border: 1px solid $gray-5;
  padding: 8px 10px;
  border-radius: 3px;
  &:focus {
    outline: none;
  }
}

#language,
#type {
  margin-right: 30px;
  color: $white;

  @include d(s) {
    margin-right: 0;
    width: 300px;
    margin-bottom: 20px;
  }
  option {
    color: $gray-6;
  }
  .light & {
    color: $gray-6;
  }
}

#btn-upload {
  button {
    background: $blue;
    border: none;
    border-radius: 3px;
    padding: 10px 20px;
    @include tipography(15);
    letter-spacing: 3px;
    text-transform: uppercase;
    font-weight: bold;
    cursor: pointer;
    &:focus {
      outline: none;
    }
  }
}

#files-type {
  .light & {
    color: $gray-6;
  }
}

@media (max-width: 575.98px) {
  #btn-upload {
    button {
      @include tipography(12);
    }
  }
  #upload-file {
    position: relative;
    margin-right: 30px;
    @include d(s) {
      margin-right: 0px;
      margin-bottom: 30px;
    }
    label {
      @include tipography(12);
    }
  }
}
</style>
